// Procedural ambient sound engine (Web Audio API). Generates subtle,
// scene-specific atmosphere in-browser — no external audio files.
// Kept intentionally quiet so it never competes with narration/dialogue.

export class AmbientEngine {
  constructor() {
    this.ctx = null;
    this.master = null;
    this.nodes = [];
    this.noiseBuffer = null;
    this.enabled = false;
    this.currentScene = null;
  }

  _ensure() {
    if (this.ctx) return;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    this.ctx = new AC();
    this.master = this.ctx.createGain();
    this.master.gain.value = 0;
    this.master.connect(this.ctx.destination);
    this._buildNoise();
  }

  _buildNoise() {
    const len = this.ctx.sampleRate * 2;
    const buffer = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    let last = 0;
    for (let i = 0; i < len; i++) {
      const white = Math.random() * 2 - 1;
      last = (last + 0.02 * white) / 1.02; // brown-ish noise
      data[i] = last * 3.5;
    }
    this.noiseBuffer = buffer;
  }

  async resume() {
    this._ensure();
    if (this.ctx && this.ctx.state === "suspended") {
      try { await this.ctx.resume(); } catch (e) {}
    }
  }

  _clearNodes() {
    this.nodes.forEach((n) => {
      try { n.stop && n.stop(); } catch (e) {}
      try { n.disconnect && n.disconnect(); } catch (e) {}
    });
    this.nodes = [];
    if (this._chirpTimer) { clearInterval(this._chirpTimer); this._chirpTimer = null; }
  }

  _noiseSource(gain, type, freq, q = 0.7) {
    const src = this.ctx.createBufferSource();
    src.buffer = this.noiseBuffer;
    src.loop = true;
    const filter = this.ctx.createBiquadFilter();
    filter.type = type;
    filter.frequency.value = freq;
    filter.Q.value = q;
    const g = this.ctx.createGain();
    g.gain.value = gain;
    src.connect(filter);
    filter.connect(g);
    g.connect(this.master);
    src.start();
    this.nodes.push(src, filter, g);
    return { g, filter };
  }

  // Slow amplitude wobble to suggest a moving crowd murmur
  _murmur(target) {
    const lfo = this.ctx.createOscillator();
    lfo.frequency.value = 0.15;
    const lfoGain = this.ctx.createGain();
    lfoGain.gain.value = target.g.gain.value * 0.6;
    lfo.connect(lfoGain);
    lfoGain.connect(target.g.gain);
    lfo.start();
    this.nodes.push(lfo, lfoGain);
  }

  _chirps() {
    this._chirpTimer = setInterval(() => {
      if (!this.enabled || Math.random() > 0.55) return;
      const o = this.ctx.createOscillator();
      o.type = "sine";
      o.frequency.value = 2600 + Math.random() * 1600;
      const g = this.ctx.createGain();
      g.gain.value = 0;
      o.connect(g);
      g.connect(this.master);
      const t = this.ctx.currentTime;
      g.gain.linearRampToValueAtTime(0.015, t + 0.04);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.28);
      o.start(t);
      o.stop(t + 0.3);
    }, 2600);
  }

  setScene(profile) {
    this._ensure();
    if (!this.ctx || !profile) return;
    this.currentScene = profile.key;
    this._clearNodes();

    switch (profile.key) {
      case "cafeteria": {
        const bed = this._noiseSource(0.05, "bandpass", 500, 0.6);
        this._murmur(bed);
        this._noiseSource(0.012, "highpass", 3000, 0.5);
        break;
      }
      case "corridor": {
        this._noiseSource(0.03, "lowpass", 900, 0.4);
        this._noiseSource(0.008, "highpass", 4000, 0.5);
        break;
      }
      case "classroom": {
        const bed = this._noiseSource(0.03, "bandpass", 420, 0.5);
        this._murmur(bed);
        break;
      }
      case "night": {
        // very low quiet room hum + faint air
        const o = this.ctx.createOscillator();
        o.type = "sine";
        o.frequency.value = 62;
        const g = this.ctx.createGain();
        g.gain.value = 0.02;
        o.connect(g); g.connect(this.master); o.start();
        this.nodes.push(o, g);
        this._noiseSource(0.006, "highpass", 6000, 0.4);
        break;
      }
      case "kitchen": {
        this._noiseSource(0.022, "lowpass", 700, 0.4);
        const o = this.ctx.createOscillator();
        o.type = "sine";
        o.frequency.value = 110;
        const g = this.ctx.createGain();
        g.gain.value = 0.01;
        o.connect(g); g.connect(this.master); o.start();
        this.nodes.push(o, g);
        break;
      }
      case "outdoor": {
        this._noiseSource(0.035, "lowpass", 600, 0.3); // gentle wind
        this._chirps();
        break;
      }
      case "calm":
      default: {
        this._noiseSource(0.02, "lowpass", 500, 0.3);
        break;
      }
    }
    this._applyGain();
  }

  // A soft, realistic phone-notification chime (two ascending tones)
  chime() {
    this._ensure();
    if (!this.ctx || !this.enabled) return;
    const t0 = this.ctx.currentTime + 0.05;
    [ [880, 0], [1320, 0.14] ].forEach(([freq, delay]) => {
      const o = this.ctx.createOscillator();
      o.type = "sine";
      o.frequency.value = freq;
      const g = this.ctx.createGain();
      g.gain.value = 0;
      o.connect(g);
      g.connect(this.master);
      const t = t0 + delay;
      g.gain.linearRampToValueAtTime(0.08, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.5);
      o.start(t);
      o.stop(t + 0.55);
    });
  }

  _applyGain() {
    if (!this.master || !this.ctx) return;
    const target = this.enabled ? 0.4 : 0;
    const now = this.ctx.currentTime;
    this.master.gain.cancelScheduledValues(now);
    this.master.gain.setValueAtTime(this.master.gain.value, now);
    this.master.gain.linearRampToValueAtTime(target, now + 0.6);
  }

  // A soft, short interaction tick (sending a message, continuing)
  click() {
    this._ensure();
    if (!this.ctx || !this.enabled) return;
    const t = this.ctx.currentTime;
    const o = this.ctx.createOscillator();
    o.type = "sine";
    o.frequency.setValueAtTime(520, t);
    o.frequency.exponentialRampToValueAtTime(320, t + 0.08);
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(0.05, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.14);
    o.connect(g);
    g.connect(this.master);
    o.start(t);
    o.stop(t + 0.16);
  }

  async setEnabled(on) {
    this.enabled = on;
    this._ensure();
    if (on) await this.resume();
    this._applyGain();
  }

  destroy() {
    this._clearNodes();
    if (this.ctx) { try { this.ctx.close(); } catch (e) {} }
    this.ctx = null;
  }

  // Stop every scene node immediately (keeps the context + enabled preference for later scenes).
  silence() {
    this._clearNodes();
    this.currentScene = null;
  }
}
